import { Component, OnInit, ChangeDetectionStrategy, ViewChild, Input } from '@angular/core';
import { bind, hasId, pathJoin, showErrorSwal, skipAuthInterceptor } from '@asamco/utils';
import { BehaviorSubject, from, Observable, of, Subject } from 'rxjs';
import notify from 'devextreme/ui/notify';
import DevExpress from 'devextreme';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { UsermanagementService } from '../../services/usermanagement.service';
import { UserManagementPermissions } from '../../enums/user-management-permissions.enum';
import { SelectedCompanyService } from '../../selected-company/selected-company.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AsamcoAuthService } from '@asamco/base';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import DataSource from 'devextreme/data/data_source';
import { NgSwitch, NgSwitchCase, AsyncPipe } from '@angular/common';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxoExportModule, DxoEditingModule, DxoSelectionModule, DxiColumnModule } from 'devextreme-angular/ui/nested';
import { DxDataGridModule } from 'devextreme-angular';
import { UserCompanyService } from '../../services/user-company.service';

@Component({
    selector: 'app-shell-user-permissions-management',
    templateUrl: './user-permissions-management.component.html',
    styleUrls: ['./user-permissions-management.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [DxDataGridModule, DxoExportModule, DxoEditingModule, DxoSelectionModule, DxiColumnModule, DxTemplateModule, NgSwitch, NgSwitchCase, AsyncPipe]
})
export class UserPermissionsManagementComponent implements OnInit {
	public readonly allowedPermissionLabels = [
		'Create links',
		'Edit links',
		'Run links',
		'Enable or disable links',
		'Delete links',
	];

  @ViewChild("usercompanygrid") dataGrid: DxDataGridComponent;

  constructor(private userManagementService: UsermanagementService, private selectedCompanyService: SelectedCompanyService, private auth: AsamcoAuthService, private http: HttpClient, private userCompanyService: UserCompanyService) { }

	@Input()
	isCompanyOwner: boolean;

	@Input()
	canAssignUserManagementPermissions: boolean;

	public get canEditPermissions() {
		return this.isCompanyOwner || this.canAssignUserManagementPermissions;
	}


  	public hasAccess: Subject<boolean> = new BehaviorSubject<boolean>(false);
	public dataSource$: Subject<DevExpress.data.DataSource> = new BehaviorSubject<DevExpress.data.DataSource>(undefined);
	public destroy$: Subject<void> = new Subject<void>();


  ngOnInit(): void {
	this.setupDataSource();
  }

  /** Making use of this hack because the DxDataGrid somehow bugs out to the point where it's unable to render it's contents
	 * Currently still unknown of why this occurs with this specific instance
	 */
	setupDataSource() {
		this.selectedCompanyService
			.getCompanyIdObservable()
			.pipe(
				switchMap((selectedCompanyId: number) => {
					return from(this.userCompanyService.GetUserControlOverview(selectedCompanyId))
				}), takeUntil(this.destroy$)
			)
			.subscribe((e: any) => {
				let dataSource = new DataSource({
					store: {
						type: 'array',
						key: 'id',
						data: e,
					},
				});
				this.dataSource$.next(dataSource)
			});
	}

  @bind
	setCellValueDelete(newData, value, currentRowData) {
		this.dataGrid.disabled = true;
		newData.canDeleteLinks = value;
		this.userManagementService.updateUserEntity(this.selectedCompanyService.getSelectedCompanyID(), currentRowData.id, value, UserManagementPermissions.deletelink).subscribe(e => {
			this.dataGrid.disabled = false;
			notify({
				message: 'Successfully saved settings',
				type: 'success',
				displayTime: 1000,
			});
		}, error => {
			this.dataGrid.disabled = false;
			showErrorSwal(error)
		});
	}

	@bind
	setCellValueEdit(newData, value, currentRowData) {
		this.dataGrid.disabled = true;
		newData.canEditLinks = value;
		this.userManagementService.updateUserEntity(this.selectedCompanyService.getSelectedCompanyID(), currentRowData.id, value, UserManagementPermissions.editlink).subscribe(e => {
			this.dataGrid.disabled = false;
			notify({
				message: 'Successfully saved settings',
				type: 'success',
				displayTime: 1000,
			});
		}, error => {
			this.dataGrid.disabled = false;
			showErrorSwal(error)
		});
	}

	@bind
	setCellValueRun(newData, value, currentRowData) {
		this.dataGrid.disabled = true;
		newData.canRunLinks = value;
		this.userManagementService.updateUserEntity(this.selectedCompanyService.getSelectedCompanyID(), currentRowData.id, value, UserManagementPermissions.runlink).subscribe(e => {
			this.dataGrid.disabled = false;
			notify({
				message: 'Successfully saved settings',
				type: 'success',
				displayTime: 1000,
			});
		}, error => {
			this.dataGrid.disabled = false;
			showErrorSwal(error)
		});
	}

	@bind
	setCellValueEnableOrDisable(newData, value, currentRowData) {
		this.dataGrid.disabled = true;
		newData.canEnableOrDisableLinks = value;
		this.userManagementService.updateUserEntity(this.selectedCompanyService.getSelectedCompanyID(), currentRowData.id, value, UserManagementPermissions.enableordisablelink).subscribe(e => {
			this.dataGrid.disabled = false;
			notify({
				message: 'Successfully saved settings',
				type: 'success',
				displayTime: 1000,
			});
		}, error => {
			this.dataGrid.disabled = false;
			showErrorSwal(error)
		});
	}

	@bind
	setCellValueCreate(newData, value, currentRowData) {
		this.dataGrid.disabled = true;
		newData.canCreateLinks = value;
		this.userManagementService.updateUserEntity(this.selectedCompanyService.getSelectedCompanyID(), currentRowData.id, value, UserManagementPermissions.createlink).subscribe(e => {
			this.dataGrid.disabled = false;
			notify({
				message: 'Successfully saved settings',
				type: 'success',
				displayTime: 1000,
			});
		}, error => {
			this.dataGrid.disabled = false;
			showErrorSwal(error)
		});
	}

	@bind
	setCellValueCspfsMockDataAccess(newData, value, currentRowData) {
		this.dataGrid.disabled = true;
		newData.cspfsMockDataAccess = value;
		this.userManagementService.updateUserEntity(this.selectedCompanyService.getSelectedCompanyID(), currentRowData.id, value, UserManagementPermissions.cspfsaccessmockdata).subscribe(e => {
			this.dataGrid.disabled = false;
			notify({
				message: 'Successfully saved settings',
				type: 'success',
				displayTime: 1000,
			});
		}, error => {
			this.dataGrid.disabled = false;
			showErrorSwal(error)
		});
	}

	@bind
	setCellValueCanViewApplicationLogs(newData, value, currentRowData) {
		this.dataGrid.disabled = true;
		newData.canViewApplicationLogs = value;
		this.userManagementService.updateUserEntity(this.selectedCompanyService.getSelectedCompanyID(), currentRowData.id, value, UserManagementPermissions.canviewapplicationlogs).subscribe(e => {
			this.dataGrid.disabled = false;
			notify({
				message: 'Successfully saved settings',
				type: 'success',
				displayTime: 1000,
			});
		}, error => {
			this.dataGrid.disabled = false;
			showErrorSwal(error)
		});
	}

	@bind
	setCellValueCspMockDataAccess(newData, value, currentRowData) {
		this.dataGrid.disabled = true;
		newData.cspMockDataAccess = value;
		this.userManagementService.updateUserEntity(this.selectedCompanyService.getSelectedCompanyID(), currentRowData.id, value, UserManagementPermissions.cspaccessmockdata).subscribe(e => {
			this.dataGrid.disabled = false;
			notify({
				message: 'Successfully saved settings',
				type: 'success',
				displayTime: 1000,
			});
		}, error => {
			this.dataGrid.disabled = false;
			showErrorSwal(error)
		});
	}

}
